import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react';
import { ErrorApi } from '../lib/api.js';

type Tipo = 'ok' | 'error' | 'aviso' | 'info';

interface Aviso {
  id: number;
  tipo: Tipo;
  texto: string;
}

interface Contexto {
  avisar(texto: string, tipo?: Tipo): void;
  /** Traduce un error de la API a un aviso legible. */
  avisarError(error: unknown): void;
}

const ContextoAvisos = createContext<Contexto | null>(null);

const ICONO = { ok: CheckCircle2, error: XCircle, aviso: AlertTriangle, info: Info } as const;

export function ProveedorAvisos({ children }: { children: ReactNode }) {
  const [avisos, setAvisos] = useState<Aviso[]>([]);

  const avisar = useCallback((texto: string, tipo: Tipo = 'ok') => {
    const id = Date.now() + Math.random();
    setAvisos((previos) => [...previos, { id, tipo, texto }]);
    // Los errores se quedan más tiempo: son los que hay que alcanzar a leer.
    setTimeout(() => setAvisos((previos) => previos.filter((a) => a.id !== id)), tipo === 'error' ? 7000 : 3500);
  }, []);

  const avisarError = useCallback(
    (error: unknown) => {
      if (error instanceof ErrorApi) {
        avisar(error.message, error.estado === 403 || error.estado === 409 ? 'aviso' : 'error');
        return;
      }
      avisar(error instanceof Error ? error.message : 'Ocurrió un error inesperado', 'error');
    },
    [avisar],
  );

  const valor = useMemo(() => ({ avisar, avisarError }), [avisar, avisarError]);

  return (
    <ContextoAvisos.Provider value={valor}>
      {children}
      <div className="avisos">
        {avisos.map((a) => {
          const Icono = ICONO[a.tipo];
          return (
            <div key={a.id} className={`aviso aviso--${a.tipo}`} role="status">
              <Icono size={15} style={{ flex: 'none', marginTop: 1 }} />
              <span>{a.texto}</span>
            </div>
          );
        })}
      </div>
    </ContextoAvisos.Provider>
  );
}

export function useAvisos(): Contexto {
  const contexto = useContext(ContextoAvisos);
  if (!contexto) throw new Error('useAvisos fuera del proveedor');
  return contexto;
}
